/**
 * WizardSlice — owns the entire pre-debate configuration flow.
 *
 * Surfaces a single coherent block (`wizard: WizardConfig`) plus discrete
 * actions for each wizard interaction. This slice is the ONLY place where
 * `/api/debate/wizard/personas` is invoked from the FE — components never
 * call the transport directly.
 */
import type { StateCreator } from "zustand";

import type { DebateTopology } from "../../domain/types";
import {
  isDynamicCouncilSource,
  type CouncilSource,
} from "../../domain/personaLibrary";
import {
  freshCandidateState,
  freshQuestionQuality,
  WIZARD_DEFAULTS,
  WIZARD_LIMITS,
  type CritiqueStrictness,
  type DecisionConstraints,
  type TerminationProfile,
  type PersonaPatch,
  type Urgency,
  type WizardConfig,
  type WizardPersona,
  type WizardStep,
} from "../../domain/wizard";
import {
  fetchCouncilCandidates,
  fetchPersonaFromTitle,
  fetchWizardPersonas,
  patchWizardPersona,
  WizardApiError,
} from "../../transport/wizardClient";
import {
  assessQuestionQuality,
  refineQuestion,
  type QuestionRefineAnswer,
} from "../../transport/questionQualityClient";
import {
  fetchDebatePersonas as apiFetchDebatePersonas,
  loadVersionTree as apiLoadVersionTree,
} from "../../transport/authClient";
import type { DebateStore } from "../debateStore";

export interface WizardCommitSnapshot {
  query: string;
  topology: DebateTopology;
  personas: string[];
  maxRounds: number;
  autoFinalize: boolean;
  critiqueStrictness: CritiqueStrictness;
  /** Konsey Cognitive Renaissance — Bölüm A0/A1. */
  terminationProfile: TerminationProfile;
  /** Görev 16 precursor — public retrieval backends must stay off. */
  privateMode: boolean;
  /** Görev 4 — urgency override; `null` = let the backend heuristic decide. */
  urgency: Urgency | null;
  /** Görev 5 — structured decision constraints. */
  constraints: DecisionConstraints;
  /** Persona Kütüphanesi — seçili kalıcı takım (yoksa null). */
  libraryTeamId: string | null;
  /** Persona Kütüphanesi — seçili organizasyon birimi (alt ağaç dahil). */
  libraryUnitId: string | null;
  /** Persona Kütüphanesi — tek tek seçilmiş kalıcı personalar. */
  libraryPersonaIds: string[];
  /** Konsey Yönergesi — bu tartışmaya özel çalışma talimatı (yoksa null). */
  userDebatePrompt: string | null;
}

export type { CouncilSource };

export interface WizardSlice {
  wizard: WizardConfig;
  /** Modal visibility — wizard is rendered inside a Radix Dialog. */
  wizardOpen: boolean;

  setStep(step: WizardStep): void;
  setQuery(q: string): void;
  setTopology(t: DebateTopology): void;
  setAgentCount(n: number): void;
  setMaxRounds(n: number): void;
  setAutoFinalize(v: boolean): void;
  setCritiqueStrictness(s: CritiqueStrictness): void;
  /** Konsey Cognitive Renaissance — Bölüm A0/A1. */
  setTerminationProfile(p: TerminationProfile): void;
  /** Görev 16 precursor. */
  setPrivateMode(v: boolean): void;
  /** Görev 4. */
  setUrgency(u: Urgency | null): void;
  /** Görev 5 — shallow-merges into the existing constraints object. */
  setConstraints(patch: Partial<DecisionConstraints>): void;
  /**
   * Konsey Yönergesi — konseyin bu soruyu NASIL işleyeceğine dair serbest
   * metin talimat. Soru metninden ayrıdır: soru "ne sorulduğunu", yönerge
   * "hangi eksende çalışılacağını" belirler. Backend'de
   * `StartDebateBody.user_debate_prompt` olarak tüm personaların sistem
   * prompt'una en yüksek öncelikle enjekte edilir.
   */
  setUserDebatePrompt(text: string): void;
  /**
   * Persona Kütüphanesi — kalıcı takım seç / temizle.
   *
   * Bir takım seçmek dinamik persona listesini de TEMİZLER: iki kaynağı
   * aynı anda göndermek konseyi sessizce ikiye katlar (wizard'ın ürettiği
   * 5 uzman + takımın 5 üyesi = 10 koltuk).
   */
  setLibraryTeam(teamId: string | null): void;
  /** Organizasyon birimi seç — alt birimler dahil tüm personalar gelir. */
  setLibraryUnit(unitId: string | null): void;
  /** Tek bir kalıcı personayı seçime ekler/çıkarır. */
  toggleLibraryPersona(personaId: string): void;
  /** Kütüphane seçimini tamamen sıfırlar (dinamik üretime geri dönüş). */
  clearLibrarySelection(): void;
  /**
   * Konsey kaynağını belirle. `"auto"` seçilmedikçe adım 3'teki otomatik
   * LLM üretimi TETİKLENMEZ — kullanıcı kütüphaneden seçme fırsatı bulmadan
   * konsey (ve maliyet) oluşmasın diye.
   */
  setCouncilSource(source: CouncilSource | null): void;

  openWizard(): void;
  closeWizard(): void;

  /* -- Soru kalite kapısı (adım 1) ---------------------------------- */
  /**
   * Soruyu iki bağımsız LLM değerlendiricisi + deterministik pipeline
   * sondasıyla puanlar. Aynı metin için taze bir rapor varsa ağ isteği
   * yapılmaz (`force` ile zorlanabilir).
   */
  assessQuestion(opts?: { force?: boolean }): Promise<void>;
  /** Bir netleştirici soruda seçenek işaretle / kaldır. */
  toggleQualityOption(questionId: string, optionId: string, allowMultiple: boolean): void;
  /** "Diğer" serbest metnini güncelle. */
  setQualityOther(questionId: string, text: string): void;
  /** Toplanan yanıtlarla LLM'e soruyu yeniden yazdır (henüz uygulamaz). */
  refineQuestionDraft(): Promise<void>;
  /** Önerilen yeniden yazımı `wizard.query`'ye uygula. */
  applyQualityProposal(): void;
  /** Öneriyi reddet, ham soruyla devam et. */
  discardQualityProposal(): void;
  /** Düşük puana rağmen devam et — bu soru için kapı bir daha sorulmaz. */
  dismissQualityGate(): void;

  generatePersonas(opts?: { seed?: number | null }): Promise<void>;
  regeneratePersona(id: string): Promise<void>;
  editPersona(id: string, patch: PersonaPatch): void;
  removePersona(id: string): void;
  manualAdd(): void;
  /**
   * AI-generate a single persona from a free-form job title and append it
   * to the council. Returns the new persona id on success so the caller
   * can open the editor / scroll into view.
   */
  addPersonaFromTitle(title: string, topic?: string | null): Promise<string>;
  /**
   * Aday konsey kataloğunu üret (`councilSource === "catalog"`).
   * `force` false ise aynı soru için üretilmiş taze katalog yeniden
   * istenmez — her adım-3 girişinde bir LLM çağrısı yakmamak için.
   */
  loadCouncilCandidates(force?: boolean): Promise<void>;
  /** Bir adayı seçime ekle/çıkar. Seçim sırası koltuk sırasıdır. */
  toggleCandidate(id: string): void;
  /** Sistemin önerdiği çekirdek kadroyu seç. */
  selectRecommendedCandidates(): void;
  /**
   * Seçili adayları gerçek personalara dönüştür. Her aday `from-title`
   * ile tek tek üretilir; kısmi başarı korunur — bir koltuk düşse bile
   * diğerleri masada kalır ve operatör eksiği elle tamamlayabilir.
   */
  materializeCandidates(): Promise<void>;
  resetWizard(): void;
  /** Snapshot the wizard state for `sessionSlice.start({...opts})`. */
  commitToDebate(): WizardCommitSnapshot;
  /**
   * Pre-fill the wizard from an existing debate's snapshot — used by the
   * fork/duplicate flow. Pulls debate_personas + session config from the
   * backend, opens the wizard at step 3 (council preview) so the operator
   * can tweak prompts before relaunching. The sessionId is set to the
   * pre-allocated fork id so `sessionSlice.start` reuses it.
   */
  prefillFromDebate(debateId: string, parentDebateId?: string): Promise<void>;
}

export type WizardSliceCreator = StateCreator<
  DebateStore,
  [["zustand/immer", never]],
  [],
  WizardSlice
>;

function freshSeed(): number {
  return Math.floor(Math.random() * 1_000_000);
}

/* ----------------------------------------------------------------------
 * Persona üretimi eşzamanlılık muhasebesi
 *
 * Store DIŞINDA tutulur: bunlar render'ı etkileyen durum değil, aynı anda
 * iki üretimin birbirini ezmesini engelleyen çalışma-zamanı muhasebesidir.
 * Store'a koymak her bilet değişiminde gereksiz yeniden render üretirdi.
 *
 * `generationTicket` monoton artar; yalnızca EN SON bileti taşıyan çağrı
 * store'a yazabilir. Böylece geç dönen bayat bir yanıt taze konseyin
 * üstüne binemez.
 * -------------------------------------------------------------------- */
let generationTicket = 0;
let inFlightGeneration = false;

function localUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback — RFC4122-like.
  const r = Math.random;
  const seg = (n: number) => Math.floor(r() * 16 ** n).toString(16).padStart(n, "0");
  return `${seg(8)}-${seg(4)}-4${seg(3)}-${seg(4)}-${seg(12)}`;
}

function manualPersona(seat: number): WizardPersona {
  return {
    id: localUuid(),
    role: "strategic-visionary",
    displayName: `Yeni Üye ${seat + 1}`,
    expertise: "Domain Uzmanı",
    systemInstruction:
      "Bu üye için sistem yönergesi. Düzenle ile özgün bir bias tanımla.",
    asymmetry: {
      sources: ["—"],
      blindSpots: ["—"],
      confidenceCeiling: 0.7,
    },
    voiceId: `voice-manual-${seat}`,
    themeColor: `hsl(${(seat * 47) % 360} 70% 56%)`,
    seatIndex: seat,
    stanceSummary: "Manuel eklenmiş üye — düzenleyerek yapılandır.",
  };
}

export const createWizardSlice: WizardSliceCreator = (set, get) => ({
  wizard: { ...WIZARD_DEFAULTS },
  wizardOpen: false,

  openWizard: () => {
    set((state) => {
      state.wizardOpen = true;
    });
  },

  closeWizard: () => {
    set((state) => {
      state.wizardOpen = false;
    });
  },

  setStep: (step) => {
    set((state) => {
      state.wizard.step = step;
    });
  },

  setQuery: (q) => {
    // Inline mirror to sessionSlice fields. Calling `get().setQuery(q)` would
    // recurse infinitely because slice composition makes wizard's setQuery
    // shadow session's at the same key.
    set((state) => {
      state.wizard.query = q;
      state.query = q;
      if (state.wizard.generationStatus === "error") {
        state.wizard.generationStatus = "idle";
      }
      // Editing the question invalidates every quality artefact. Showing a
      // score computed for a DIFFERENT string is worse than showing none —
      // the operator would read a stale 82 next to a question they just
      // gutted. `isQualityReportFresh` also guards the render path; this
      // reset keeps the store itself honest.
      if (state.wizard.quality.assessedQuery !== q.trim()) {
        state.wizard.quality = freshQuestionQuality();
      }
    });
  },

  /* -- Soru kalite kapısı ---------------------------------------------- */

  assessQuestion: async (opts) => {
    const query = get().wizard.query.trim();
    if (query.length < WIZARD_LIMITS.queryMin) return;

    const current = get().wizard.quality;
    if (current.status === "assessing") return;
    if (!opts?.force && current.report !== null && current.assessedQuery === query) {
      return;
    }

    set((state) => {
      state.wizard.quality.status = "assessing";
      state.wizard.quality.error = null;
      state.wizard.quality.proposal = null;
    });

    try {
      const report = await assessQuestionQuality(query);
      set((state) => {
        // Guard against a slow response landing after the operator kept
        // typing — the report belongs to the string it was computed for.
        if (state.wizard.query.trim() !== query) return;
        state.wizard.quality.report = report;
        state.wizard.quality.assessedQuery = query;
        state.wizard.quality.status = "ready";
        state.wizard.quality.answers = {};
        state.wizard.quality.dismissed = report.verdict === "ready";
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      set((state) => {
        state.wizard.quality.status = "error";
        state.wizard.quality.error = message;
      });
    }
  },

  toggleQualityOption: (questionId, optionId, allowMultiple) => {
    set((state) => {
      const draft = state.wizard.quality.answers[questionId] ?? { selected: [], other: "" };
      const isOn = draft.selected.includes(optionId);
      if (allowMultiple) {
        draft.selected = isOn
          ? draft.selected.filter((id) => id !== optionId)
          : [...draft.selected, optionId];
      } else {
        // Single-choice behaves like a radio group that can be un-picked.
        draft.selected = isOn ? [] : [optionId];
      }
      state.wizard.quality.answers[questionId] = draft;
    });
  },

  setQualityOther: (questionId, text) => {
    set((state) => {
      const draft = state.wizard.quality.answers[questionId] ?? { selected: [], other: "" };
      draft.other = text;
      state.wizard.quality.answers[questionId] = draft;
    });
  },

  refineQuestionDraft: async () => {
    const w = get().wizard;
    const report = w.quality.report;
    const query = w.query.trim();
    if (!report || w.quality.status === "refining") return;

    const answers: QuestionRefineAnswer[] = report.questions
      .map((q) => {
        const draft = w.quality.answers[q.id];
        if (!draft) return null;
        const labels = q.options
          .filter((o) => draft.selected.includes(o.id))
          .map((o) => o.label);
        const other = draft.other.trim();
        if (labels.length === 0 && !other) return null;
        return { questionId: q.id, question: q.question, selected: labels, other };
      })
      .filter((a): a is QuestionRefineAnswer => a !== null);

    set((state) => {
      state.wizard.quality.status = "refining";
      state.wizard.quality.error = null;
    });

    try {
      const proposal = await refineQuestion(query, answers);
      set((state) => {
        state.wizard.quality.proposal = proposal;
        state.wizard.quality.status = "ready";
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      set((state) => {
        state.wizard.quality.status = "error";
        state.wizard.quality.error = message;
      });
    }
  },

  applyQualityProposal: () => {
    const proposal = get().wizard.quality.proposal;
    if (!proposal) return;
    const next = proposal.refined.trim();
    set((state) => {
      state.wizard.query = next;
      state.query = next;
      // The refine endpoint already re-scored the rewritten text, so we
      // adopt that report wholesale instead of burning two more LLM calls.
      state.wizard.quality = {
        ...freshQuestionQuality(),
        status: "ready",
        report: proposal.report,
        assessedQuery: next,
        // The operator engaged with the gate and accepted a rewrite —
        // making them clear it a second time is hostile. Only a still-vague
        // result (score < 50) keeps asking.
        dismissed: proposal.report.verdict !== "vague",
      };
    });
  },

  discardQualityProposal: () => {
    set((state) => {
      state.wizard.quality.proposal = null;
    });
  },

  dismissQualityGate: () => {
    set((state) => {
      state.wizard.quality.dismissed = true;
      state.wizard.quality.proposal = null;
    });
  },

  setTopology: (t) => {
    set((state) => {
      state.wizard.topology = t;
      state.topology = t;
      if (state.wizard.generationStatus === "error") {
        state.wizard.generationStatus = "idle";
      }
    });
  },

  setAgentCount: (n) => {
    // DEFENSIVE: range/Slider widgets occasionally hand us NaN
    // (touch jitter, keyboard `e`, parseInt edge cases). Letting it
    // through corrupts `wizard.agentCount` and causes the entire
    // step-3 render tree to collapse: `Array.from({length: NaN})`
    // produces zero skeletons, the persona request hangs, and the
    // empty-state branch never matches → the wizard renders a
    // blank panel with only the header visible. We coerce hard.
    const v = Number(n);
    const safe = Number.isFinite(v) ? Math.round(v) : WIZARD_DEFAULTS.agentCount;
    set((state) => {
      state.wizard.agentCount = Math.max(
        WIZARD_LIMITS.agentCountMin,
        Math.min(WIZARD_LIMITS.agentCountMax, safe),
      );
      if (state.wizard.generationStatus === "error") {
        state.wizard.generationStatus = "idle";
      }
    });
  },

  setMaxRounds: (n) => {
    const v = Number(n);
    const safe = Number.isFinite(v) ? Math.round(v) : WIZARD_DEFAULTS.maxRounds;
    set((state) => {
      state.wizard.maxRounds = Math.max(
        WIZARD_LIMITS.maxRoundsMin,
        Math.min(WIZARD_LIMITS.maxRoundsMax, safe),
      );
    });
  },

  setAutoFinalize: (v) => {
    set((state) => {
      state.wizard.autoFinalize = v;
    });
  },

  setCritiqueStrictness: (s) => {
    set((state) => {
      state.wizard.critiqueStrictness = s;
    });
  },

  setTerminationProfile: (p) => {
    set((state) => {
      state.wizard.terminationProfile = p;
    });
  },

  setPrivateMode: (v) => {
    set((state) => {
      state.wizard.privateMode = v;
    });
  },

  setUrgency: (u) => {
    set((state) => {
      state.wizard.urgency = u;
    });
  },

  setConstraints: (patch) => {
    set((state) => {
      state.wizard.constraints = { ...state.wizard.constraints, ...patch };
      // Strip keys the operator cleared back to an empty string so an
      // empty budget field doesn't get sent as `{budget: ""}` (which
      // would suppress the backend's clarifying question just as
      // effectively as a real answer — an empty string must NOT count
      // as "answered").
      for (const key of Object.keys(state.wizard.constraints) as Array<
        keyof DecisionConstraints
      >) {
        const v = state.wizard.constraints[key];
        if (typeof v === "string" && v.trim().length === 0) {
          delete state.wizard.constraints[key];
        }
      }
    });
  },

  setUserDebatePrompt: (text) => {
    set((state) => {
      const trimmed = text.trim();
      state.wizard.userDebatePrompt = trimmed.length > 0 ? text : null;
    });
  },

  generatePersonas: async (opts) => {
    const seed = opts?.seed ?? freshSeed();
    const wizard = get().wizard;
    if (wizard.query.trim().length < 12) {
      set((state) => {
        state.wizard.generationError = "Sorgu en az 12 karakter olmalı.";
        state.wizard.generationStatus = "error";
      });
      return;
    }
    // Eşzamanlılık koruması. Önceden ne uçuş-halinde (in-flight) kilidi ne
    // de sıra numarası vardı: otomatik tetikleyici ile elle "Konseyi Üret"
    // aynı anda çalıştığında ikinci çağrı `generating`i yeniden yazıyor,
    // geç dönen eski yanıt da taze sonucun üstüne biniyordu. Sonuç,
    // personalar dolu olduğu hâlde spinner'ın kapanmamasıydı.
    if (inFlightGeneration) return;
    const ticket = ++generationTicket;
    inFlightGeneration = true;
    set((state) => {
      state.wizard.generationStatus = "generating";
      state.wizard.generationError = null;
      state.wizard.generationSeed = seed;
    });
    // Adopt the wizard's pendingSessionId (minted by the step-1
    // RagDropZone interaction) so the backend persona generator can
    // pull operator-uploaded context documents from the SAME session
    // pool. When no documents have been uploaded the id is still
    // safe — RagService.search_pool returns [] and the generator
    // falls back to the topic-only prompt.
    const sid = get().pendingSessionId ?? get().sessionId ?? null;
    try {
      const result = await fetchWizardPersonas({
        query: wizard.query.trim(),
        topology: wizard.topology,
        agent_count: wizard.agentCount,
        seed,
        ...(sid ? { session_id: sid } : {}),
      });
      // Honour seatOrder; reassign seatIndex sequentially.
      const ordered = result.personas.slice().sort((a, b) => {
        const ai = result.seatOrder.indexOf(a.id);
        const bi = result.seatOrder.indexOf(b.id);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
      const reseated = ordered.map((p, i) => ({ ...p, seatIndex: i }));
      if (ticket !== generationTicket) return;
      set((state) => {
        state.wizard.personas = reseated;
        state.wizard.generationStatus = "ready";
      });
    } catch (err) {
      const message =
        err instanceof WizardApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      if (ticket !== generationTicket) return;
      set((state) => {
        state.wizard.generationError = message;
        state.wizard.generationStatus = "error";
      });
    } finally {
      // `finally` şart: try/catch içinde erken `return` eden her yol
      // (bayat bilet) aksi hâlde kilidi açık bırakır ve sihirbaz bir daha
      // hiç üretim yapamaz.
      if (ticket === generationTicket) {
        inFlightGeneration = false;
        set((state) => {
          if (state.wizard.generationStatus === "generating") {
            // Buraya düşmek, `set` çağrılarından hiçbirinin çalışmadığı
            // anlamına gelir (beklenmeyen bir kaçış yolu). Spinner'ı açık
            // bırakmaktansa personaların varlığına göre karar ver.
            state.wizard.generationStatus =
              state.wizard.personas.length > 0 ? "ready" : "idle";
          }
        });
      }
    }
  },

  regeneratePersona: async (id) => {
    // Strategy: re-run the entire endpoint with a fresh seed; if the regenerated
    // set contains a persona at the same seat index, swap just that slot.
    const before = get().wizard.personas;
    const target = before.find((p) => p.id === id);
    if (!target) return;
    if (inFlightGeneration) return;
    const seed = freshSeed();
    const ticket = ++generationTicket;
    inFlightGeneration = true;
    set((state) => {
      state.wizard.generationStatus = "generating";
    });
    const sid = get().pendingSessionId ?? get().sessionId ?? null;
    try {
      const result = await fetchWizardPersonas({
        query: get().wizard.query.trim(),
        topology: get().wizard.topology,
        agent_count: get().wizard.agentCount,
        seed,
        ...(sid ? { session_id: sid } : {}),
      });
      const fresh =
        result.personas.find((p) => p.role === target.role) ??
        result.personas[target.seatIndex] ??
        result.personas[0]!;
      const swapped = before.map((p) =>
        p.id === id ? { ...fresh, seatIndex: p.seatIndex } : p,
      );
      if (ticket !== generationTicket) return;
      set((state) => {
        state.wizard.personas = swapped;
        state.wizard.generationStatus = "ready";
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (ticket !== generationTicket) return;
      set((state) => {
        state.wizard.generationError = message;
        state.wizard.generationStatus = "error";
      });
    } finally {
      if (ticket === generationTicket) {
        inFlightGeneration = false;
        set((state) => {
          if (state.wizard.generationStatus === "generating") {
            state.wizard.generationStatus =
              state.wizard.personas.length > 0 ? "ready" : "idle";
          }
        });
      }
    }
  },

  editPersona: (id, patch) => {
    // Patches are SHALLOW MERGES: anything missing from the patch is
    // preserved. Every PersonaPatch field has an explicit propagation
    // path so a drawer "Save" never silently drops a setting.
    set((state) => {
      const idx = state.wizard.personas.findIndex((p) => p.id === id);
      if (idx === -1) return;
      const current = state.wizard.personas[idx]!;
      const nextExpertise = patch.expertise ?? current.expertise;
      const next = {
        ...current,
        displayName: patch.displayName ?? current.displayName,
        role: patch.role ?? current.role,
        ...(nextExpertise ? { expertise: nextExpertise } : {}),
        systemInstruction: patch.systemInstruction ?? current.systemInstruction,
        themeColor: patch.themeColor ?? current.themeColor,
        asymmetry: patch.asymmetry ?? current.asymmetry,
        stanceSummary: patch.stanceSummary ?? current.stanceSummary,
        ...(patch.voiceRate !== undefined ? { voiceRate: patch.voiceRate } : {}),
        ...(patch.voicePitch !== undefined ? { voicePitch: patch.voicePitch } : {}),
        ...(patch.voiceVolume !== undefined ? { voiceVolume: patch.voiceVolume } : {}),
      } as typeof current;
      // exactOptionalPropertyTypes: optional fields must be EITHER
      // present-with-value OR fully absent — never present-with-
      // undefined. The "clear override" path deletes the property.
      if (patch.modelId !== undefined) {
        const trimmed = patch.modelId.trim();
        if (trimmed) {
          next.modelId = trimmed;
        } else {
          delete (next as { modelId?: string }).modelId;
        }
      }
      if (patch.samplingOverrides !== undefined) {
        // Strip subfields that the operator cleared to undefined so the
        // wire payload is minimal and the engine falls back to the
        // model catalogue defaults for those knobs.
        //
        // DEFENSIVE: the patch may legally arrive as `null` (DB rows
        // store NULL when no override is set; a fresh fork hydrates
        // the persona with `samplingOverrides: null`). Treat null and
        // non-object values as "clear the override" rather than
        // crashing on `Object.entries(null)`.
        const so = patch.samplingOverrides;
        type SO = NonNullable<typeof patch.samplingOverrides>;
        if (so == null || typeof so !== "object") {
          delete (next as { samplingOverrides?: SO }).samplingOverrides;
        } else {
          const cleaned: Record<string, number> = {};
          for (const [k, v] of Object.entries(so) as [
            keyof SO,
            number | undefined,
          ][]) {
            if (typeof v === "number" && Number.isFinite(v)) {
              cleaned[k as string] = v;
            }
          }
          if (Object.keys(cleaned).length > 0) {
            next.samplingOverrides = cleaned as SO;
          } else {
            delete (next as { samplingOverrides?: SO }).samplingOverrides;
          }
        }
      }
      state.wizard.personas[idx] = next;
    });

    // Fire-and-forget PATCH so the operator's edit lands in the
    // canonical `macdm.personas` row before they hit "Tartışmayı
    // Başlat". Failures only emit a console warning — the local
    // state remains updated, and on the next debate run the engine
    // would just read the previous DB values; a toast warns later
    // if persistence couldn't succeed.
    const updated = get().wizard.personas.find((p) => p.id === id);
    if (updated) {
      const body = {
        ...(patch.displayName !== undefined
          ? { displayName: patch.displayName }
          : {}),
        ...(patch.expertise !== undefined
          ? { expertise: patch.expertise }
          : {}),
        ...(patch.systemInstruction !== undefined
          ? { systemInstruction: patch.systemInstruction }
          : {}),
        ...(patch.stanceSummary !== undefined
          ? { stanceSummary: patch.stanceSummary }
          : {}),
        ...(patch.themeColor !== undefined
          ? { themeColor: patch.themeColor }
          : {}),
        ...(patch.voiceRate !== undefined
          ? { voiceRate: patch.voiceRate }
          : {}),
        ...(patch.voicePitch !== undefined
          ? { voicePitch: patch.voicePitch }
          : {}),
        ...(patch.voiceVolume !== undefined
          ? { voiceVolume: patch.voiceVolume }
          : {}),
        ...(patch.modelId !== undefined ? { modelId: patch.modelId } : {}),
        ...(patch.samplingOverrides !== undefined
          ? { samplingOverrides: updated.samplingOverrides ?? {} }
          : {}),
      };
      if (Object.keys(body).length > 0) {
        void patchWizardPersona(id, body).catch((err: unknown) => {
          // Persisting the edit is best-effort. Local state already
          // reflects the change so the operator's UI still feels
          // responsive; we just log so degraded rounds are debuggable.
          // eslint-disable-next-line no-console
          console.warn(
            "[wizard] persona patch persist failed:",
            err instanceof Error ? err.message : String(err),
          );
        });
      }
    }
  },

  removePersona: (id) => {
    set((state) => {
      if (state.wizard.personas.length <= state.wizard.agentCount) return;
      state.wizard.personas = state.wizard.personas
        .filter((p) => p.id !== id)
        .map((p, i) => ({ ...p, seatIndex: i }));
    });
  },

  manualAdd: () => {
    set((state) => {
      const seat = state.wizard.personas.length;
      state.wizard.personas.push(manualPersona(seat));
    });
  },

  addPersonaFromTitle: async (title, topic) => {
    const t = title.trim();
    if (t.length < 2) {
      throw new WizardApiError("En az 2 karakterlik bir unvan gir.", 400);
    }
    const seatIndex = get().wizard.personas.length;
    const totalSeats = Math.max(get().wizard.agentCount, seatIndex + 1);
    const queryHint = (topic ?? get().wizard.query ?? "").trim() || null;
    try {
      const persona = await fetchPersonaFromTitle({
        title: t,
        topic: queryHint,
        seat_index: seatIndex,
        total_seats: totalSeats,
        // Tek tek ekleme yolunda tahtadaki adlar BİLİNİYOR; göndermek
        // sunucunun çakışmayı kesin olarak çözmesini sağlar.
        taken_names: (get().wizard.personas ?? [])
          .map((p) => p.displayName)
          .filter((n): n is string => Boolean(n)),
      });
      set((state) => {
        state.wizard.personas.push({
          ...persona,
          seatIndex,
        });
      });
      return persona.id;
    } catch (err) {
      const msg =
        err instanceof WizardApiError ? err.message : (err as Error).message;
      set((state) => {
        state.wizard.generationError = msg;
      });
      throw err;
    }
  },

  loadCouncilCandidates: async (force = false) => {
    const w = get().wizard;
    const query = w.query.trim();
    if (query.length < WIZARD_LIMITS.queryMin) {
      set((state) => {
        state.wizard.candidates.status = "error";
        state.wizard.candidates.error =
          `Aday üretmek için soru en az ${WIZARD_LIMITS.queryMin} karakter olmalı.`;
      });
      return;
    }
    const cached =
      w.candidates.status === "ready" && w.candidates.builtForQuery === query;
    if (cached && !force) return;
    if (w.candidates.status === "loading") return;

    set((state) => {
      state.wizard.candidates.status = "loading";
      state.wizard.candidates.error = null;
    });
    try {
      const result = await fetchCouncilCandidates({
        query,
        count: 24,
        session_id: get().sessionId ?? null,
        directive: w.userDebatePrompt,
      });
      set((state) => {
        state.wizard.candidates.status = "ready";
        state.wizard.candidates.items = result.candidates;
        state.wizard.candidates.categories = result.categories;
        state.wizard.candidates.builtForQuery = query;
        state.wizard.candidates.error = null;
        // Yeni katalog geldiğinde eski seçim anlamsızdır (id'ler başlıktan
        // türer, farklı bir slate'te aynı id başka bir uzmanı gösterebilir).
        state.wizard.candidates.selected = result.candidates
          .filter((c) => c.recommended)
          .map((c) => c.id);
      });
    } catch (err) {
      const msg =
        err instanceof WizardApiError ? err.message : (err as Error).message;
      set((state) => {
        state.wizard.candidates.status = "error";
        state.wizard.candidates.error = msg;
      });
    }
  },

  toggleCandidate: (id) => {
    set((state) => {
      const sel = state.wizard.candidates.selected;
      const at = sel.indexOf(id);
      if (at >= 0) {
        sel.splice(at, 1);
      } else if (sel.length < WIZARD_LIMITS.agentCountMax) {
        sel.push(id);
      }
    });
  },

  selectRecommendedCandidates: () => {
    set((state) => {
      state.wizard.candidates.selected = state.wizard.candidates.items
        .filter((c) => c.recommended)
        .map((c) => c.id);
    });
  },

  materializeCandidates: async () => {
    const w = get().wizard;
    const chosen = w.candidates.selected
      .map((id) => w.candidates.items.find((c) => c.id === id))
      .filter((c): c is NonNullable<typeof c> => Boolean(c));
    if (chosen.length < WIZARD_LIMITS.agentCountMin) {
      set((state) => {
        state.wizard.candidates.error =
          `En az ${WIZARD_LIMITS.agentCountMin} üye seç.`;
      });
      return;
    }
    set((state) => {
      state.wizard.candidates.error = null;
      state.wizard.candidates.materializedCount = 0;
      state.wizard.generationStatus = "generating";
      state.wizard.generationError = null;
      state.wizard.personas = [];
    });

    const topic = w.query.trim();
    const total = chosen.length;
    // Sıralı değil paralel: 6 koltuk × ~8 sn ardışık çalışırsa operatör
    // bir dakika boş ekrana bakar. `allSettled` kısmi başarıyı korur.
    // Paralel olduğu için hiçbir çağrı diğerinin ürettiği adı göremez;
    // ad tekilliğini sunucu `seat_index`ten türetiyor. Yine de tahtada
    // önceden duran üyeler varsa onları bildiriyoruz.
    const alreadySeated = (get().wizard.personas ?? [])
      .map((p) => p.displayName)
      .filter((n): n is string => Boolean(n));
    const results = await Promise.allSettled(
      chosen.map((candidate, seatIndex) =>
        fetchPersonaFromTitle({
          title: candidate.title,
          topic,
          seat_index: seatIndex,
          total_seats: total,
          taken_names: alreadySeated,
        }).then((persona) => {
          set((state) => {
            state.wizard.candidates.materializedCount += 1;
          });
          return { persona, seatIndex };
        }),
      ),
    );

    const built = results
      .filter(
        (r): r is PromiseFulfilledResult<{ persona: WizardPersona; seatIndex: number }> =>
          r.status === "fulfilled",
      )
      .map((r) => r.value)
      .sort((a, b) => a.seatIndex - b.seatIndex)
      .map(({ persona, seatIndex }) => ({ ...persona, seatIndex }));

    const failed = total - built.length;
    set((state) => {
      state.wizard.personas = built;
      state.wizard.agentCount = Math.max(
        WIZARD_LIMITS.agentCountMin,
        Math.min(WIZARD_LIMITS.agentCountMax, built.length),
      );
      state.wizard.generationStatus = built.length > 0 ? "ready" : "error";
      state.wizard.generationError =
        built.length === 0
          ? "Seçilen adaylardan hiçbiri üretilemedi. Tekrar deneyin."
          : failed > 0
            ? `${failed} üye üretilemedi; kalan ${built.length} üye masada. ` +
              "Eksikleri manuel ekleyebilirsin."
            : null;
      state.wizard.candidates.materializedCount = built.length;
    });
  },

  resetWizard: () => {
    set((state) => {
      state.wizard = { ...WIZARD_DEFAULTS, candidates: freshCandidateState() };
    });
  },

  setCouncilSource: (source) => {
    set((state) => {
      const previous = state.wizard.councilSource;
      state.wizard.councilSource = source;
      // Kaynak değiştirmek önceki kaynağın seçimini temizler; aksi halde
      // "takımdan birime geçtim ama eski takım hâlâ gönderiliyor" sessiz
      // hatası oluşur (backend ikisini de konuşlandırır).
      if (source !== "team") state.wizard.libraryTeamId = null;
      if (source !== "unit") state.wizard.libraryUnitId = null;
      if (source !== "picked") state.wizard.libraryPersonaIds = [];
      // `auto` ve `catalog` aynı aileden — ikisi de `personas`'ı doldurur —
      // ama BİRBİRİNE geçişte de liste temizlenmeli, yoksa katalogdan
      // kurulan kadro otomatik üretimin üstüne biner.
      if (!isDynamicCouncilSource(source) || source !== previous) {
        state.wizard.personas = [];
        state.wizard.generationStatus = "idle";
        state.wizard.generationError = null;
      }
      if (source !== "catalog") {
        state.wizard.candidates = freshCandidateState();
      }
    });
  },

  setLibraryTeam: (teamId) => {
    set((state) => {
      state.wizard.libraryTeamId = teamId;
      if (teamId) {
        state.wizard.councilSource = "team";
        state.wizard.libraryUnitId = null;
        state.wizard.personas = [];
        state.wizard.generationStatus = "idle";
        state.wizard.generationError = null;
      }
    });
  },

  setLibraryUnit: (unitId) => {
    set((state) => {
      state.wizard.libraryUnitId = unitId;
      if (unitId) {
        state.wizard.councilSource = "unit";
        state.wizard.libraryTeamId = null;
        state.wizard.personas = [];
        state.wizard.generationStatus = "idle";
        state.wizard.generationError = null;
      }
    });
  },

  toggleLibraryPersona: (personaId) => {
    set((state) => {
      const current = state.wizard.libraryPersonaIds ?? [];
      const idx = current.indexOf(personaId);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(personaId);
        // Aynı çift-konsey riski (bkz. `setLibraryTeam`).
        state.wizard.personas = [];
        state.wizard.generationStatus = "idle";
        state.wizard.generationError = null;
      }
      state.wizard.libraryPersonaIds = current;
      if (current.length > 0 && state.wizard.councilSource === null) {
        state.wizard.councilSource = "picked";
      }
    });
  },

  clearLibrarySelection: () => {
    set((state) => {
      state.wizard.councilSource = null;
      state.wizard.libraryTeamId = null;
      state.wizard.libraryUnitId = null;
      state.wizard.libraryPersonaIds = [];
    });
  },

  commitToDebate: () => {
    const w = get().wizard;
    return {
      query: w.query.trim(),
      topology: w.topology,
      personas: w.personas.map((p) => p.id),
      maxRounds: w.maxRounds,
      autoFinalize: w.autoFinalize,
      critiqueStrictness: w.critiqueStrictness,
      terminationProfile: w.terminationProfile,
      privateMode: w.privateMode,
      urgency: w.urgency,
      constraints: w.constraints,
      libraryTeamId: w.libraryTeamId,
      libraryUnitId: w.libraryUnitId,
      libraryPersonaIds: [...(w.libraryPersonaIds ?? [])],
      userDebatePrompt: w.userDebatePrompt?.trim() ? w.userDebatePrompt.trim() : null,
    };
  },

  prefillFromDebate: async (debateId, parentDebateId) => {
    set((state) => {
      state.wizard.generationStatus = "generating";
      state.wizard.generationError = null;
    });
    try {
      const [snapshots, tree] = await Promise.all([
        apiFetchDebatePersonas(debateId),
        apiLoadVersionTree(debateId).catch(() => ({
          tree: [],
          root_id: debateId,
        })),
      ]);

      // Find this debate's row in the tree to recover query + topology
      // metadata. Falls back to safe defaults if the row is missing.
      const me = tree.tree.find((v) => v.id === debateId);
      const wizardPersonas: WizardPersona[] = snapshots.map((s) => ({
        id: s.canonical_id ?? s.id,
        role: (s.role as WizardPersona["role"]) ?? "strategic-visionary",
        displayName: s.display_name,
        ...(s.honorific && (s.honorific === "Bey" || s.honorific === "Hanım")
          ? { honorific: s.honorific as "Bey" | "Hanım" }
          : {}),
        ...(s.expertise ? { expertise: s.expertise } : {}),
        systemInstruction: s.system_instr,
        asymmetry: {
          sources: Array.isArray((s.asymmetry as { sources?: string[] })?.sources)
            ? ((s.asymmetry as { sources?: string[] }).sources ?? [])
            : [],
          blindSpots: Array.isArray(
            (s.asymmetry as { blindSpots?: string[] })?.blindSpots,
          )
            ? ((s.asymmetry as { blindSpots?: string[] }).blindSpots ?? [])
            : [],
          confidenceCeiling: Number(
            (s.asymmetry as { confidenceCeiling?: number })?.confidenceCeiling ??
              0.75,
          ),
        },
        voiceId: s.voice_id ?? `voice-${s.role}-${s.seat_index.toString().padStart(2, "0")}`,
        themeColor: s.theme_color ?? `hsl(${(s.seat_index * 47) % 360} 70% 56%)`,
        seatIndex: s.seat_index,
        ...(s.model_id ? { modelId: s.model_id } : {}),
        stanceSummary: s.stance_summary ?? "",
      }));

      set((state) => {
        state.wizard.personas = wizardPersonas;
        state.wizard.agentCount = wizardPersonas.length;
        state.wizard.parentDebateId = parentDebateId ?? null;
        if (me?.query) {
          state.wizard.query = me.query;
          state.query = me.query;
        }
        // Open at step 3 (council preview) so the operator immediately
        // lands on the persona list and can edit prompts before launch.
        state.wizard.step = 3;
        // Fork akışında konsey ZATEN elimizde. Kaynağı "auto" işaretlemezsek
        // adım 3 kaynak-seçim ekranını gösterip getirdiğimiz personaları
        // gizler — çatallama akışında görünür bir regresyon olurdu.
        state.wizard.councilSource = "auto";
        state.wizard.libraryTeamId = null;
        state.wizard.libraryUnitId = null;
        state.wizard.libraryPersonaIds = [];
        state.wizard.generationStatus = "ready";
        state.wizardOpen = true;
        // Hand the forked row to `start()` through the STAGING slot, not
        // `sessionId`. `start()` adopts `pendingSessionId` and ignores
        // `sessionId` entirely, so writing the fork id there meant every
        // launch minted a fresh uuid and abandoned the row the fork had
        // just created — the lineage never grew a second node and the
        // operator saw "fork butonu çalışmıyor". Staging is also the
        // correct semantics: this debate has not started yet, and any
        // document dropped in the wizard before launch must land on the
        // same id the engine will run under.
        state.pendingSessionId = debateId;
        state.sessionId = null;
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      set((state) => {
        state.wizard.generationStatus = "error";
        state.wizard.generationError = message;
      });
    }
  },
});
